import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/BankAccountsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the bank accounts list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['bankAccounts', customerId, currentToken]` — a bare string plus a page
// token, which makes the cache page-shaped rather than list-shaped: every page
// landed under its own key, so invalidating "the list" meant invalidating one
// page.
//
// ⚠️ Every key below is scoped by `customerId`. Bank accounts belong to a
// customer — `listBankAccounts` REQUIRES the filter — so two customers' lists
// must never share a cache entry. Dropping customerId from the key would serve
// customer A's accounts to customer B, which is a data-exposure bug rather
// than a staleness one, and these are banking details.
export const bankAccountsKeys = {
    all: ['bank-accounts'] as const,
    lists: () => [...bankAccountsKeys.all, 'list'] as const,
    list: (customerId: string, filters: Record<string, string>) =>
        [...bankAccountsKeys.lists(), customerId, {filters}] as const,
    details: () => [...bankAccountsKeys.all, 'detail'] as const,
    detail: (customerId: string, id: string) =>
        [...bankAccountsKeys.details(), customerId, id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which read
 * as compliant (a query hook, a page control, forward/back buttons) while being
 * a page-at-a-time model the shared footer cannot describe.
 * `useInfiniteQuery` concatenates pages instead, so the client holds a growing
 * loaded window and the footer can honestly say how much of it it has seen.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 *
 * Every filter is in the queryKey (§9): omit one and changing it silently
 * serves the previous filter's cache.
 */
export const useBankAccountsInfiniteQuery = (
    customerId: string,
    filters: Record<string, string> = {},
) => {
    return useInfiniteQuery({
        queryKey: bankAccountsKeys.list(customerId, filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(customerId, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
        // The list is meaningless without a customer, and `fetchRecords` would
        // send `filter: {customerId: undefined}` — which the API answers with
        // every bank account it can see. Gate rather than let that request
        // leave the browser.
        enabled: !!customerId,
    });
};

/**
 * Batch delete. The list key is invalidated on success so the table reflects
 * the removal without a manual reload (§8).
 */
export const useDeleteBankAccountsMutation = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(customerId, id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Bank account deleted'
                    : `${ids.length} bank accounts deleted`,
            );
            queryClient.invalidateQueries({queryKey: bankAccountsKeys.all});
        },
        onError: notifyError,
    });
};
